/** KYC document categories for closed-deal post-sales uploads */
export const KYC_CATEGORIES = {
  PAN: 'pan',
  AADHAAR: 'aadhaar',
  VOTER_ID: 'voter_id',
  OTHER: 'other'
};

export const KYC_CATEGORY_LABELS = {
  pan: 'PAN card',
  aadhaar: 'Aadhaar card',
  voter_id: 'Voter ID',
  other: 'Other document'
};

/** Secondary ID: user picks one of these (required alongside PAN). */
export const SECONDARY_ID_OPTIONS = [
  { id: KYC_CATEGORIES.AADHAAR, label: 'Aadhaar' },
  { id: KYC_CATEGORIES.VOTER_ID, label: 'Voter ID' }
];

export function normalizeKycCategory(attachment = {}) {
  const rawCategory = String(attachment?.category || '')
    .toLowerCase()
    .trim();
  if (rawCategory === KYC_CATEGORIES.PAN) return KYC_CATEGORIES.PAN;
  if (rawCategory === KYC_CATEGORIES.AADHAAR) return KYC_CATEGORIES.AADHAAR;
  if (rawCategory === KYC_CATEGORIES.VOTER_ID) return KYC_CATEGORIES.VOTER_ID;
  if (rawCategory === KYC_CATEGORIES.OTHER) return KYC_CATEGORIES.OTHER;

  // Backfill/fallback for legacy rows where category was not persisted.
  const label = String(attachment?.name || '')
    .toLowerCase()
    .trim();
  if (label.includes('pan')) return KYC_CATEGORIES.PAN;
  if (label.includes('aadhaar') || label.includes('aadhar')) return KYC_CATEGORIES.AADHAAR;
  if (label.includes('voter')) return KYC_CATEGORIES.VOTER_ID;
  return KYC_CATEGORIES.OTHER;
}

/** Newer of two attachments — ids are sequential, created_at is the fallback. */
function isNewerKycAttachment(a, b) {
  const aId = Number(a?.id);
  const bId = Number(b?.id);
  if (Number.isFinite(aId) && Number.isFinite(bId) && aId !== bId) return aId > bId;
  const aAt = Date.parse(a?.created_at || '');
  const bAt = Date.parse(b?.created_at || '');
  if (Number.isFinite(aAt) && Number.isFinite(bAt)) return aAt > bAt;
  return false;
}

/**
 * Uploads are append-only — "Replace" adds another row instead of overwriting —
 * so a slot can hold several attempts. Only the newest one is meaningful, and it
 * is the one the backend rolls the deal KYC status up from (latest id per category).
 */
export function pickLatestKycAttachment(attachments = []) {
  let latest = null;
  for (const attachment of attachments) {
    if (!latest || isNewerKycAttachment(attachment, latest)) latest = attachment;
  }
  return latest;
}

export function evaluateKycCompleteness(attachments = []) {
  const byCategory = (cat) => attachments.filter((a) => normalizeKycCategory(a) === cat);
  const hasPan = byCategory(KYC_CATEGORIES.PAN).length > 0;
  const hasAadhaar = byCategory(KYC_CATEGORIES.AADHAAR).length > 0;
  const hasVoter = byCategory(KYC_CATEGORIES.VOTER_ID).length > 0;
  const secondaryOk = hasAadhaar || hasVoter;
  return {
    hasPan,
    hasAadhaar,
    hasVoter,
    secondaryOk,
    complete: hasPan && secondaryOk,
    missing: [
      !hasPan ? 'PAN card' : null,
      !secondaryOk ? 'Aadhaar or Voter ID' : null
    ].filter(Boolean)
  };
}

/** Deal-level number match status from GET .../kyc-status */
export function evaluateKycVerification(kycStatus) {
  const status = String(kycStatus?.kyc_verify_status || 'pending').toLowerCase();
  const labels = {
    pending: 'Not verified',
    extracting: 'Verifying…',
    verified: 'KYC verified',
    mismatch: 'Mismatch',
    failed: 'Verification failed'
  };
  return {
    status,
    verified: status === 'verified',
    extracting: status === 'extracting',
    mismatch: status === 'mismatch',
    failed: status === 'failed',
    label: labels[status] || labels.pending,
    detail: kycStatus?.kyc_verify_detail || null,
    documents: kycStatus?.documents || [],
    applicant: kycStatus?.applicant || null,
    completeness: kycStatus?.completeness || null
  };
}

export function matchStatusLabel(matchStatus) {
  const s = String(matchStatus || 'pending').toLowerCase();
  if (s === 'matched') return { label: 'Matched', tone: 'emerald' };
  if (s === 'mismatch') return { label: 'Mismatch', tone: 'rose' };
  if (s === 'failed') return { label: 'Failed', tone: 'rose' };
  if (s === 'skipped') return { label: 'Skipped', tone: 'slate' };
  if (s === 'pending') return { label: 'Verifying…', tone: 'amber' };
  return { label: s, tone: 'slate' };
}

export function kycVerifyBadgeClasses(status) {
  const s = String(status || 'pending').toLowerCase();
  if (s === 'verified') return 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200';
  if (s === 'extracting') return 'bg-sky-50 text-sky-800 ring-1 ring-sky-200';
  if (s === 'mismatch' || s === 'failed') return 'bg-rose-50 text-rose-800 ring-1 ring-rose-200';
  return 'bg-slate-50 text-slate-700 ring-1 ring-slate-200';
}

/**
 * One row per applicant_number. Failed Application Form saves used to insert
 * another primary buyer on every click; the list and "N on file" must agree.
 */
export function uniqueApplicantsBySlot(rows = []) {
  const list = Array.isArray(rows) ? rows : [];
  const byNumber = new Map();
  for (const row of list) {
    const num = Number(row?.applicant_number) || 0;
    const prev = byNumber.get(num);
    if (!prev || Number(row.id) > Number(prev.id)) {
      byNumber.set(num, row);
    }
  }
  return Array.from(byNumber.values()).sort(
    (a, b) => (a.applicant_number || 0) - (b.applicant_number || 0)
  );
}
